import { Dropdown, DropdownProps } from "./Dropdown"
import cc from "classcat"
import { useEffect, useRef, useState, KeyboardEvent } from "react"

interface Option {
  value: string
  label: string
}

interface Props extends DropdownProps {
  id: string
  value?: string | number | readonly string[] | undefined
  emptyLabel?: string
  options: Option[]
  onChange: (option: Option) => void
}

interface OptionProps extends Option {
  onChange: (option: Option) => void
  onChangeFocus: (dir: "next" | "prev") => void
  close: () => void
  selected: boolean
  focused: boolean
}

const DropdownSelectOption = ({
  value,
  label,
  onChange,
  onChangeFocus,
  selected,
  focused,
  close,
}: OptionProps) => {
  const listElement = useRef<HTMLButtonElement>(null)

  const handleChange = () => {
    onChange({ value, label })
    close()
  }

  useEffect(() => {
    if (focused) listElement?.current?.focus()
    else listElement?.current?.blur()
  }, [focused, listElement])

  const handleKeyDown = (e: KeyboardEvent) => {
    e.preventDefault()
    switch (e.key) {
      case "Enter":
        handleChange()
        return
      case "ArrowUp":
        onChangeFocus("prev")
        return
      case "ArrowDown":
        onChangeFocus("next")
        return
      case "Escape":
        close()
        return
      default:
        return
    }
  }

  return (
    <button
      ref={listElement}
      role="option"
      aria-selected={selected}
      onClick={handleChange}
      className={cc([
        "block-list__item",
        selected && "block-list__item--selected",
      ])}
      onKeyDown={handleKeyDown}
    >
      {label}
    </button>
  )
}

export const DropdownSelect = ({
  id,
  value,
  emptyLabel = "Select one",
  options,
  valueLabel,
  onChange,
}: Props) => {
  const [focusedKey, setFocusedKey] = useState<number | null>(0)
  const label = valueLabel || (value as string) || emptyLabel

  const handleChangeFocus = (dir: "next" | "prev") => {
    let nextIndex: number | null
    if (focusedKey === null) {
      if (dir === "prev") nextIndex = null
      else nextIndex = 0
    } else {
      if (dir === "next") {
        if (focusedKey === options.length - 1) nextIndex = focusedKey
        else nextIndex = focusedKey + 1
      } else {
        if (focusedKey === 0) nextIndex = focusedKey
        else nextIndex = focusedKey - 1
      }
    }
    setFocusedKey(nextIndex)
  }

  return (
    <Dropdown valueLabel={label} id={id}>
      {(props) => {
        return (
          <div className="block-list" role="listbox">
            {options.map((option, i) => {
              const selected = value === option.value
              const focused = focusedKey !== null ? focusedKey === i : selected
              return (
                <DropdownSelectOption
                  value={option.value}
                  label={option.label}
                  selected={selected}
                  focused={focused}
                  key={option.value}
                  onChange={onChange}
                  onChangeFocus={handleChangeFocus}
                  close={props.close}
                />
              )
            })}
          </div>
        )
      }}
    </Dropdown>
  )
}
